import { Assets, Rectangle, Texture } from 'pixi.js';
import { fetchGameTextureManifest, releaseGameTextureManifest, tryResolveGameAssetFile, type GameTextureManifest } from './resolveGameAssetUrl';
import { acquireVerifiedAssetUrl, releaseAssetPath, type AssetUrlLease } from './release-assets';
import { getRequiredSpriteKeys } from './spriteMapping';
import { resolveManifestAssetId, resolveManifestTexture } from './manifest-bindings.mjs';

// Pixi Assets is a process-wide cache: unloading one renderer's URL must never
// destroy a source still referenced by another renderer or an animation atlas.
interface SharedSource {
  owners: Set<object>;
  promise: Promise<Texture>;
  controller: AbortController;
  texture?: Texture;
  assetLease?: AssetUrlLease;
  unloading?: Promise<void>;
}
const sharedSources = new Map<string, SharedSource>();
let activeDecodes = 0;
const decodeWaiters: Array<() => void> = [];
const pendingSourceReleases = new Set<Promise<void>>();
async function acquireDecodeSlot(signal: AbortSignal): Promise<void> {
  if (signal.aborted) throw new Error('Sprite source request cancelled');
  if (activeDecodes < 2) { activeDecodes++; return; }
  await new Promise<void>((resolve, reject) => {
    const resume = () => { signal.removeEventListener('abort', cancel); resolve(); };
    const cancel = () => {
      const index = decodeWaiters.indexOf(resume);
      if (index >= 0) decodeWaiters.splice(index, 1);
      reject(new Error('Sprite source request cancelled'));
    };
    signal.addEventListener('abort', cancel, { once: true });
    decodeWaiters.push(resume);
  });
}
async function shrinkSource(texture: Texture, maxDimension?: number): Promise<Texture> {
  const source = texture.source;
  const width = source.width, height = source.height;
  if (!maxDimension || Math.max(source.pixelWidth ?? width, source.pixelHeight ?? height) <= maxDimension) return texture;
  const resource = source.resource as ImageBitmap;
  const ratio = maxDimension / Math.max(width, height);
  const pixelWidth = Math.max(1, Math.round(width * ratio));
  const pixelHeight = Math.max(1, Math.round(height * ratio));
  let resized: ImageBitmap | HTMLCanvasElement | undefined;
  if (typeof createImageBitmap === 'function') {
    try { resized = await createImageBitmap(resource, { resizeWidth: pixelWidth, resizeHeight: pixelHeight, resizeQuality: 'high' }); }
    catch { /* Older WebKit versions can reject bitmap resize options; use Canvas below. */ }
  }
  if (!resized && typeof document !== 'undefined') {
    const canvas = document.createElement('canvas');
    canvas.width = pixelWidth; canvas.height = pixelHeight;
    const context = canvas.getContext('2d');
    if (context) { context.drawImage(resource, 0, 0, pixelWidth, pixelHeight); resized = canvas; }
  }
  if (!resized) return texture;
  source.resource = resized;
  source.resize(width, height, ratio);
  // Pixi rounds physical dimensions in resize. Restore exact manifest-space
  // dimensions so edge frames/UVs retain the original, including odd PNG sizes.
  source.width = width; source.height = height;
  texture.update();
  resource.close?.();
  return texture;
}

async function decodeSource(url: string, signal: AbortSignal, maxDimension: number | undefined, entry: SharedSource): Promise<Texture> {
  await acquireDecodeSlot(signal);
  try {
    // Do not accumulate decoded resources behind slow asynchronous unloads.
    await Promise.resolve();
    while (pendingSourceReleases.size) await Promise.all([...pendingSourceReleases]);
    if (signal.aborted) throw new Error('Sprite source request cancelled');
    if (releaseAssetPath(url)) {
      const lease = await acquireVerifiedAssetUrl(url, signal);
      entry.assetLease = lease;
      try {
        if (signal.aborted) throw new Error('Sprite source request cancelled');
        return await shrinkSource(await Assets.load<Texture>({ src: lease.url, loadParser: 'loadTextures' }), maxDimension);
      } catch (error) { lease.release(); throw error; }
    }
    return await shrinkSource(await Assets.load<Texture>(url), maxDimension);
  }
  finally {
    const waiter = decodeWaiters.shift();
    if (waiter) waiter();
    else activeDecodes--;
  }
}

export async function acquireSpriteSource(url: string, owner: object, maxDimension?: number): Promise<Texture> {
  let entry = sharedSources.get(url);
  if (entry?.unloading) {
    await entry.unloading;
    return acquireSpriteSource(url, owner, maxDimension);
  }
  if (!entry) {
    const controller = new AbortController();
    entry = { owners: new Set([owner]), controller, promise: undefined! };
    const created = entry;
    entry.promise = decodeSource(url, controller.signal, maxDimension, created).then(texture => { created.texture = texture; return texture; });
    sharedSources.set(url, entry);
    void entry.promise.catch(() => {
      if (sharedSources.get(url) === created) sharedSources.delete(url);
    });
  }
  entry.owners.add(owner);
  return entry.promise;
}

export function releaseSpriteSource(url: string, owner: object): Promise<void> {
  const entry = sharedSources.get(url);
  if (!entry || !entry.owners.delete(owner) || entry.owners.size) return Promise.resolve();
  if (entry.unloading) return entry.unloading;
  entry.controller.abort();
  entry.unloading = entry.promise.then(async texture => {
    // A disposed in-flight request only becomes releasable after decode. Enter
    // the barrier now so subsequent decodes cannot accumulate behind its unload.
    pendingSourceReleases.add(entry.unloading!);
    const resource = texture.source.resource as { close?: () => void; width?: number; height?: number } | undefined;
    try { await Assets.unload(entry.assetLease?.url ?? url); }
    finally {
      entry.assetLease?.release();
      // Pixi ImageSource.destroy nulls resource but does not close ImageBitmap.
      if (resource?.close) resource.close();
      else if (resource && typeof HTMLCanvasElement !== 'undefined' && resource instanceof HTMLCanvasElement) {
        resource.width = 0; resource.height = 0;
      }
    }
  }, () => undefined).catch(error => {
    console.warn(`[Assets] Could not unload sprite source: ${url}`, error);
  }).then(() => {
    pendingSourceReleases.delete(entry.unloading!);
    if (sharedSources.get(url) === entry) sharedSources.delete(url);
  });
  // Only already decoded resources enter this barrier; pending loads cannot
  // block their own semaphore slot while cancellation waits for completion.
  if (entry.texture) pendingSourceReleases.add(entry.unloading);
  return entry.unloading;
}

export interface SpriteRegistryOptions {
  /** null disables byte-budget eviction; source and decode safety limits still apply. */
  maxResidentBytes?: number | null;
  maxConcurrentLoads?: number;
  maxQueuedLoads?: number;
  retryDelayMs?: number;
  maxSourceDimension?: number;
}
interface ResidentSource { texture: Texture; bytes: number; lastUsed: number; }

export class GameSpriteRegistry {
  private _manifest: GameTextureManifest | null = null;
  private _manifestController: AbortController | null = null;
  private _frameTextures = new Map<string, Texture>();
  private _sources = new Map<string, ResidentSource>();
  // One Pixi source can back multiple URL aliases and arbitrary frame views.
  private _sourceUrls = new Map<Texture['source'], Set<string>>();
  private _queue = new Set<string>();
  private _loading = new Map<string, object>();
  private _owners = new Map<string, object>();
  private _failedUntil = new Map<string, number>();
  private _errors = new Map<string, Error>();
  private _admissionRefused = new Set<string>();
  private _pins = new Set<string>();
  private _demand = new Set<string>();
  private _collecting = false;
  private _ready = false;
  private _revision = 0;
  private _generation = 0;
  private _clock = 0;
  private _admissionRejections = 0;
  private readonly limits: Omit<Required<SpriteRegistryOptions>, 'maxResidentBytes'> & { maxResidentBytes: number | null };

  constructor(options: SpriteRegistryOptions = {}) {
    this.limits = {
      maxResidentBytes: options.maxResidentBytes === null ? null : Math.max(1, options.maxResidentBytes ?? 64 * 1024 * 1024),
      maxConcurrentLoads: Math.max(1, Math.floor(options.maxConcurrentLoads ?? 2)),
      maxQueuedLoads: Math.max(1, Math.floor(options.maxQueuedLoads ?? 64)),
      retryDelayMs: Math.max(0, options.retryDelayMs ?? 5000),
      maxSourceDimension: Math.max(1, options.maxSourceDimension ?? 768),
    };
  }
  get revision(): number {
    // Stationary renderers cache missing candidates by this revision and do not
    // call getTexture again. Expiring a failed admission/load must invalidate
    // those caches, otherwise its cooldown can never produce another request.
    if (this._failedUntil.size === 0) return this._revision;
    let retryable = false;
    const now = Date.now();
    for (const [url, until] of this._failedUntil) {
      if (until <= now) { this._failedUntil.delete(url); retryable = true; }
    }
    if (retryable) this._revision++;
    return this._revision;
  }
  /** Readiness follows this viewport demand, including queued/retry/overbudget sources. */
  get frameError(): Error | undefined { return [...this._demand].map(url => this._errors.get(url)).find(error => !!error); }
  get frameAdmissionError(): Error | undefined {
    return [...this._demand].some(url => this._admissionRefused.has(url))
      ? new Error('Visible texture cannot fit the startup GPU budget; reload required') : undefined;
  }
  get frameReady(): boolean { return this._ready && [...this._demand].every(url => this._sources.has(url)); }
  get ready(): boolean { return this._ready; }
  get manifest(): GameTextureManifest | null { return this._manifest; }
  get stats() {
    return { residentBytes: this.residentBytes(), residentSources: this._sources.size,
      queued: this._queue.size, inFlight: this._loading.size,
      resizedSources: [...this._sources.values()].filter(({ texture }) => texture.source.pixelWidth < texture.source.width || texture.source.pixelHeight < texture.source.height).length,
      maxResidentBytes: this.limits.maxResidentBytes, admissionRejections: this._admissionRejections };
  }

  /** Historical name retained for callers; initialization fetches JSON only. */
  async loadAll(manifestUrl?: string): Promise<void> {
    this.dispose();
    const generation = this._generation;
    this._manifestController = new AbortController();
    const manifest = await fetchGameTextureManifest(manifestUrl, this._manifestController.signal);
    if (generation !== this._generation) { releaseGameTextureManifest(manifest); return; }
    this._manifest = manifest;
    for (const key of getRequiredSpriteKeys()) {
      if (!resolveManifestTexture(manifest, key)) console.warn(`[Assets] Missing manifest entry for sprite key: ${key}`);
    }
    this._ready = true;
  }

  beginFrame(): void { this._collecting = true; this._demand.clear(); }

  /** Retain cached/live Sprite references even on frames that skip redraw. */
  retainTexture(texture: Texture): void {
    const urls = this._sourceUrls.get(texture.source);
    if (urls) for (const url of urls) this._demand.add(url);
  }

  /** Call after replacing/resetting stale Sprite references, before rendering. */
  endFrame(): void {
    this._pins = new Set(this._demand);
    this._collecting = false;
    for (const url of this._queue) if (!this._pins.has(url)) this._queue.delete(url);
    this.evictToFit(0);
    this.pump();
  }

  dispose(): void {
    this._generation++;
    this._ready = false;
    this._manifestController?.abort(); this._manifestController = null;
    if (this._manifest) releaseGameTextureManifest(this._manifest);
    this._manifest = null;
    for (const texture of this._frameTextures.values()) texture.destroy(false);
    this._frameTextures.clear();
    this._sources.clear();
    this._sourceUrls.clear();
    this._queue.clear();
    this._loading.clear();
    this._failedUntil.clear(); this._errors.clear(); this._admissionRefused.clear();
    this._pins.clear(); this._demand.clear(); this._collecting = false;
    for (const [url, owner] of this._owners) releaseSpriteSource(url, owner);
    this._owners.clear();
  }

  getTexture(key: string): Texture | null {
    const assetId = resolveManifestAssetId(this._manifest, key);
    const entry = assetId ? this._manifest?.textures[assetId] : undefined;
    const url = entry && tryResolveGameAssetFile(entry.file, this._manifest ?? undefined);
    if (!assetId || !entry || !url || !this._ready) return null;
    this._demand.add(url);
    if (!this._collecting) this._pins.add(url);
    const resident = this._sources.get(url);
    if (!resident) {
      if (!this._loading.has(url) && !this._queue.has(url) &&
          (this._failedUntil.get(url) ?? 0) <= Date.now() && this._queue.size < this.limits.maxQueuedLoads) {
        this._queue.add(url);
        this.pump();
      }
      return null;
    }
    resident.lastUsed = ++this._clock;
    const frame = this._manifest?.useBindings?.[key]?.frame ?? entry.frame;
    if (!frame) return resident.texture;
    const { x, y, width, height } = frame;
    if (![x, y, width, height].every(Number.isSafeInteger) || x < 0 || y < 0 || width <= 0 || height <= 0 ||
        x + width > resident.texture.source.width || y + height > resident.texture.source.height) return null;
    const cacheKey = `${url}:${x},${y},${width},${height}`;
    let texture = this._frameTextures.get(cacheKey);
    if (!texture) {
      texture = new Texture({ source: resident.texture.source, frame: new Rectangle(x, y, width, height) });
      this._frameTextures.set(cacheKey, texture);
    }
    return texture;
  }

  private residentBytes(): number {
    let bytes = 0;
    for (const source of this._sources.values()) bytes += source.bytes;
    return bytes;
  }
  private evictToFit(incoming: number): boolean {
    if (this.limits.maxResidentBytes === null) return true;
    const candidates = [...this._sources.entries()]
      .filter(([url]) => !this._pins.has(url) && !this._demand.has(url))
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [url] of candidates) {
      if (this.residentBytes() + incoming <= this.limits.maxResidentBytes) break;
      this.evict(url);
    }
    return this.residentBytes() + incoming <= this.limits.maxResidentBytes;
  }
  private evict(url: string): void {
    for (const [key, texture] of this._frameTextures) {
      if (key.startsWith(`${url}:`)) { texture.destroy(false); this._frameTextures.delete(key); }
    }
    const source = this._sources.get(url)?.texture.source;
    if (source) {
      const urls = this._sourceUrls.get(source);
      urls?.delete(url);
      if (!urls?.size) this._sourceUrls.delete(source);
    }
    this._sources.delete(url);
    const owner = this._owners.get(url);
    if (owner) releaseSpriteSource(url, owner);
    this._owners.delete(url);
    this._revision++;
  }
  private pump(): void {
    while (this._ready && this._loading.size < this.limits.maxConcurrentLoads && this._queue.size) {
      const url = this._queue.values().next().value!;
      this._queue.delete(url);
      const owner = {};
      const generation = this._generation;
      this._owners.set(url, owner);
      this._loading.set(url, owner);
      this._admissionRefused.delete(url);
      void this.load(url, owner, generation);
    }
  }
  private async load(url: string, owner: object, generation: number): Promise<void> {
    try {
      let texture: Texture;
      try { texture = await acquireSpriteSource(url, owner, this.limits.maxSourceDimension); }
      catch {
        if (generation !== this._generation) return;
        texture = await acquireSpriteSource(url, owner, this.limits.maxSourceDimension);
      }
      if (generation !== this._generation) return;
      const bytes = (texture.source.pixelWidth ?? texture.source.width) * (texture.source.pixelHeight ?? texture.source.height) * 4;
      if (!Number.isFinite(bytes) || bytes <= 0 || !this.evictToFit(bytes)) {
        this._admissionRejections++;
        this._admissionRefused.add(url);
        this._failedUntil.set(url, Date.now() + this.limits.retryDelayMs);
        return;
      }
      texture.source.scaleMode = 'nearest';
      this._sources.set(url, { texture, bytes, lastUsed: ++this._clock });
      let urls = this._sourceUrls.get(texture.source);
      if (!urls) this._sourceUrls.set(texture.source, urls = new Set());
      urls.add(url);
      this._revision++;
    } catch (error) {
      if (generation === this._generation) {
        this._failedUntil.set(url, Date.now() + this.limits.retryDelayMs);
        if (releaseAssetPath(url)) this._errors.set(url, new Error(`Pinned game asset unavailable; reload required: ${url}: ${String(error)}`));
      }
    } finally {
      if (generation !== this._generation || !this._sources.has(url)) {
        releaseSpriteSource(url, owner);
        if (this._owners.get(url) === owner) this._owners.delete(url);
      }
      if (this._loading.get(url) === owner) this._loading.delete(url);
      this.pump();
    }
  }
}
